import React from 'react';
import { Icon } from '@barracuda-internal/bds-core';
import Google from '@barracuda-internal/bds-core/dist/BrandLogos/Google';
import Microsoft from '@barracuda-internal/bds-core/dist/BrandLogos/Microsoft';
import { PROVIDER_ICON, PROVIDER_LABEL } from './providerIcon';

// Resolution order per slug:
//   1. Local SVG at `ui/src/assets/providers/{slug}.svg`
//   2. simple-icons named export `si{Slug}` (auto-discovered)
//   3. BDS BrandLogos entry (Google → gemini, Microsoft → azure_openai / copilot)
//   4. Generic BDS icon from providerIcon.PROVIDER_ICON
//
// See `ui/src/assets/providers/README.md` for how to add real SVGs at step 1.

// --- Step 1: local SVG discovery via webpack require.context ---
// In Jest / non-webpack contexts require.context is undefined; guard so
// the module loads cleanly under tests.
const localSvgs = {};
try {
    // eslint-disable-next-line no-undef
    const ctx = require.context('assets/providers', false, /\.svg$/);
    ctx.keys().forEach((key) => {
        const slug = key.replace(/^\.\//, '').replace(/\.svg$/, '').toLowerCase();
        const mod = ctx(key);
        localSvgs[slug] = typeof mod === 'string' ? mod : mod.default;
    });
} catch (_e) {
    // fine — no local SVGs yet, will fall through
}

// --- Step 2: simple-icons lookup ---
// simple-icons v16 exposes named exports like `siAnthropic`, `siMistralai`.
// We import the whole module (tree-shaken lazily by webpack — the bulk of
// the dataset is a plain data table) and pick out what we need.
let simpleIconsModule = null;
try {
    simpleIconsModule = require('simple-icons'); // eslint-disable-line global-require
} catch (_e) {
    simpleIconsModule = null;
}

function simpleIconFor(slug) {
    if (!simpleIconsModule) return null;
    // "openai" -> "siOpenai", "azure_openai" -> "siAzureOpenai" would need
    // manual casing; luckily the slugs we care about are single-word or
    // camelCased already in simple-icons. Compute the export name:
    const parts = slug.split(/[_-]/);
    const camel = parts.map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1))).join('');
    const key = 'si' + camel.charAt(0).toUpperCase() + camel.slice(1);
    const icon = simpleIconsModule[key];
    return icon && icon.path && icon.hex ? icon : null;
}

// --- Step 3: BDS BrandLogos map (well-known vendors already licensed) ---
const BDS_BRAND = {
    gemini:       Google,
    azure_openai: Microsoft,
    copilot:      Microsoft,
};

// --- Step 4 (before generic BDS icon): monogram fallback ---
// For providers where no clean-license logo exists anywhere. Uses a
// letter-in-a-circle glyph with a distinct color per slug so ADMIN scanning
// through history can still tell one from another at a glance. Colors are
// intentionally generic (not brand-exact) so this stays a clear fallback
// rather than an attempt at logo mimicry.
const PROVIDER_MONOGRAM = {
    openai:    { letter: 'O', bg: '#1e293b' },
    chatgpt:   { letter: 'G', bg: '#0f766e' },
    groq:      { letter: 'G', bg: '#ea580c' },
    xai:       { letter: 'X', bg: '#111827' },
    amazon:    { letter: 'A', bg: '#b45309' },
    cohere:    { letter: 'C', bg: '#166534' },
    together:  { letter: 'T', bg: '#1d4ed8' },
    fireworks: { letter: 'F', bg: '#be123c' },
};

const DEFAULT_SIZE = 20;

function SimpleIconSvg({ icon, size, title }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill={`#${icon.hex}`}
            role="img"
            aria-label={title || icon.title}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
        >
            <path d={icon.path} />
        </svg>
    );
}

function MonogramSvg({ config, size, title }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            role="img"
            aria-label={title}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
        >
            <circle cx="12" cy="12" r="12" fill={config.bg} />
            <text
                x="12"
                y="12"
                fill="#ffffff"
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily="-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif"
                fontWeight="700"
                fontSize="14"
            >
                {config.letter}
            </text>
        </svg>
    );
}

/**
 * ProviderBrand — render the correct brand mark for an LLM provider slug.
 *
 * Props:
 *   slug — provider slug from the scan_events row (e.g. 'openai', 'anthropic', 'gemini')
 *   size — pixel size (default 20)
 */
export default function ProviderBrand({ slug, size = DEFAULT_SIZE }) {
    const label = PROVIDER_LABEL[slug] || slug || 'Unknown';

    // 1. Local SVG asset (highest priority — designer-provided official logo)
    if (slug && localSvgs[slug]) {
        return (
            <img
                src={localSvgs[slug]}
                width={size}
                height={size}
                alt={label}
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
            />
        );
    }

    // 2. simple-icons match
    if (slug) {
        const icon = simpleIconFor(slug);
        if (icon) return <SimpleIconSvg icon={icon} size={size} title={label} />;
    }

    // 3. BDS BrandLogo (Google, Microsoft)
    const BdsBrand = slug && BDS_BRAND[slug];
    if (BdsBrand) {
        return <BdsBrand style={{ width: size, height: size, verticalAlign: 'middle' }} />;
    }

    // 4. Monogram — colored letter-in-a-circle for slugs where no real logo
    // ships in any of the licensed sources. Distinct at a glance but obviously
    // not a real brand mark.
    const monogram = slug && PROVIDER_MONOGRAM[slug];
    if (monogram) {
        return <MonogramSvg config={monogram} size={size} title={label} />;
    }

    // 5. Generic BDS icon fallback (unknown, json_parse, anything unmapped)
    const IconCmp = PROVIDER_ICON[slug] || PROVIDER_ICON.unknown;
    return <Icon bdsIcon={IconCmp} bdsSize="small" />;
}
